// Teacher dashboard export: CLASS-LEVEL aggregates only.
//
// Exactly what the class overview already shows (counts per chart type, per day,
// per support level, per escalation reason, per question category, and the DBR
// class aggregates) — never a per-student row, never a research ID, a date finer
// than the on-screen MM/DD, a message or an image. Per-student export is a PI
// decision and is deliberately not offered here.

// A cell that starts with one of these is executed as a formula by spreadsheet
// apps; prefix it so the file is inert when opened.
const FORMULA_START = /^[=+\-@\t\r]/;

export const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  let text = String(value);
  if (FORMULA_START.test(text)) text = `'${text}`;
  if (/[",\n\r]/.test(text)) text = `"${text.replace(/"/g, '""')}"`;
  return text;
};

const rowsFrom = (section, items) => (Array.isArray(items) ? items : [])
  .map((item) => [section, item.label, item.value]);

export const buildClassSummaryRows = (classOverviewData, dbrSummary, t) => {
  const rows = [[t('dashboard.teacher.export.colSection'), t('dashboard.teacher.export.colItem'), t('dashboard.teacher.export.colCount')]];
  const data = classOverviewData || {};
  rows.push(...rowsFrom(t('dashboard.teacher.weakGraphTypes'), data.graphTypes));
  rows.push(...rowsFrom(t('dashboard.teacher.recentProgress'), data.progress));
  rows.push(...rowsFrom(t('dashboard.teacher.supportLevelDistClass'), data.styles));
  rows.push(...rowsFrom(t('dashboard.teacher.escalationDist'), data.cognitive));
  rows.push(...rowsFrom(t('dashboard.teacher.faqCategories'), data.faq));

  const agg = dbrSummary?.class_aggregates;
  if (agg) {
    (agg.weekly_mechanism_usage || []).forEach((week) => {
      ['stage_change', 'keyword_click', 'graph_element_click'].forEach((key) => {
        rows.push([
          t('dashboard.teacher.dbrMechanismUsage'),
          `${week.week} ${t('dashboard.teacher.dbrMechanism.' + key)}`,
          week[key] ?? 0,
        ]);
      });
    });
    Object.entries(agg.trigger_reason_distribution || {}).forEach(([reason, count]) => {
      rows.push([
        t('dashboard.teacher.dbrTriggerDistribution'),
        reason === 'other' ? t('dashboard.teacher.dbrOtherLabel') : t('dashboard.cog.' + reason, { defaultValue: reason }),
        count,
      ]);
    });
    Object.entries(agg.effective_support_level_distribution || {}).forEach(([level, count]) => {
      rows.push([
        t('dashboard.teacher.dbrSupportLevelDistribution'),
        t('dashboard.supportLevel.Lv' + level, { defaultValue: 'Lv' + level }),
        count,
      ]);
    });
  }
  return rows;
};

export const rowsToCsv = (rows) => rows.map((row) => row.map(csvCell).join(',')).join('\r\n');

// UTF-8 with BOM so spreadsheet apps on Windows/macOS read Japanese correctly.
export const downloadCsv = (filename, csv) => {
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
